import React from 'react';

interface StepIndicatorProps {
  steps: { name: string }[];
  currentStep: number;
  onStepChange?: (step: number) => void;
}

/**
 * Direction A step register.
 *
 * Detent squares on a DASHED hairline. Labels are sentence-case
 * (`1 Produkt`, `3 Maße`) — no zero-pad, no all-caps transformation, so
 * German diacritics (`ß`, `ä`, `ö`) survive intact. This is the
 * finish-reviewer's Fix 7 (dashed connector) + Fix 8a (label form).
 *
 * Desktop: hairline from left to right with one small square detent per
 * step, each labeled `<index> <step-name>` below. Detent state grammar:
 * hairline (pending), filled ink (visited), filled rubric (active).
 *
 * Mobile: the label line collapses; only the detent row and the
 * current step's name below remain.
 */
export default function StepIndicator({ steps, currentStep, onStepChange }: StepIndicatorProps) {
  const currentName = steps[currentStep]?.name ?? '';

  return (
    <nav aria-label="Konfigurationsschritte" className="flex-1 min-w-0 max-w-[720px] mx-auto text-ink">
      <ol className="flex items-center justify-between gap-2">
        {steps.map((step, idx) => {
          const isCurrent = idx === currentStep;
          const isVisited = idx < currentStep;
          const isClickable = isVisited && !!onStepChange;

          const detentClass = isCurrent
            ? 'is-active'
            : isVisited
              ? 'is-visited'
              : '';

          const handleClick = () => {
            if (isClickable) onStepChange!(idx);
          };

          return (
            <React.Fragment key={step.name}>
              <li className="flex flex-col items-center min-w-0">
                <button
                  type="button"
                  onClick={handleClick}
                  disabled={!isClickable}
                  aria-current={isCurrent ? 'step' : undefined}
                  aria-label={`Schritt ${idx + 1}: ${step.name}`}
                  className={`group flex flex-col items-center gap-1.5 ${
                    isClickable ? 'cursor-pointer' : 'cursor-default'
                  }`}
                >
                  <span
                    className={`sv-detent ${detentClass} transition-colors ${
                      isClickable ? 'group-hover:border-rubric group-focus-visible:border-rubric' : ''
                    }`}
                    aria-hidden
                  />
                  {/* Desktop-only label — sentence case, no zero-pad, keeps ß/ä/ö. */}
                  <span
                    className={`hidden md:inline-flex items-baseline gap-1.5 whitespace-nowrap transition-colors text-[13px] ${
                      isCurrent
                        ? 'text-ink'
                        : isVisited
                          ? 'text-ink-2 group-hover:text-rubric'
                          : 'text-ink-3'
                    }`}
                    style={{
                      fontFamily: "'Config Condensed', sans-serif",
                      textTransform: 'none',
                      letterSpacing: '0.01em',
                    }}
                  >
                    <span
                      className="sv-mono text-[13px] opacity-70"
                      style={{ letterSpacing: 0 }}
                    >
                      {idx + 1}
                    </span>
                    <span>{step.name}</span>
                  </span>
                </button>
              </li>
              {idx < steps.length - 1 && (
                <li aria-hidden className="flex-1 min-w-4">
                  {/* Dashed hairline connector — the CAD-grammar detail
                      the reviewer's Fix 7 asked for. Rendered as SVG so
                      the dash pattern is device-pixel exact. */}
                  <svg
                    className="block w-full h-px -translate-y-2 md:-translate-y-3"
                    viewBox="0 0 100 1"
                    preserveAspectRatio="none"
                    aria-hidden
                  >
                    <line
                      x1="0"
                      y1="0.5"
                      x2="100"
                      y2="0.5"
                      stroke="currentColor"
                      strokeOpacity="0.5"
                      strokeWidth="1"
                      strokeDasharray="1.6 2.4"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                </li>
              )}
            </React.Fragment>
          );
        })}
      </ol>
      <div className="md:hidden mt-2 text-center text-[13px] text-ink" style={{ fontFamily: "'Config Condensed', sans-serif" }}>
        <span className="sv-mono text-[13px] opacity-70 mr-1.5">{currentStep + 1}</span>
        {currentName}
      </div>
    </nav>
  );
}
